import { useCallback, useRef, useState, type RefObject } from "react";
import { returnFocus } from "../../primitives";

export interface NoteComposerState {
  readonly open: boolean;
  readonly draft: string;
  readonly canSubmit: boolean;
  readonly setDraft: (value: string) => void;
  /** Open when closed, close when open. */
  readonly toggle: () => void;
  /** Close, drop the draft, and return focus to the trigger. */
  readonly close: () => void;
  /** Save a non-empty draft, then close. */
  readonly submit: () => void;
  readonly triggerRef: RefObject<HTMLButtonElement>;
}

/**
 * State of an inline note composer. The trigger and the field render in
 * different places (header row, then the row below), so the hook owns the
 * state and `NoteComposerTrigger` / `NoteComposerRow` render it.
 */
export function useNoteComposer(onSave: (body: string) => void): NoteComposerState {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setDraft("");
    if (triggerRef.current) returnFocus(triggerRef.current);
  }, []);

  const submit = useCallback(() => {
    const body = draft.trim();
    if (body.length === 0) return;
    onSave(body);
    close();
  }, [draft, onSave, close]);

  const toggle = useCallback(() => {
    if (open) close();
    else setOpen(true);
  }, [open, close]);

  return {
    open,
    draft,
    canSubmit: draft.trim().length > 0,
    setDraft,
    toggle,
    close,
    submit,
    triggerRef,
  };
}
